import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Table, Form, Container, Row, Col, Button } from 'react-bootstrap';
import PaginationComponent from '../Pagination/PaginationComponent';
import { getAllPersons } from '../../services/Person/personService';
import CreatePersonModal from './CreatePersonModal';
import EditPersonModal from './EditPersonModal';
import PersonFilter from '../Filters/PersonFilter';
import { FaEdit } from 'react-icons/fa';
import { usePermissions } from '../../hooks/usePermissions';
import { usePermissionsContext } from '../../context/PermissionsContext';

const mapPersonTypes = () => ({
  includeCustomer: true,
  includeSupplier: true,
  includeDriver: true,
  includeProducer: true,
  active: true,
});

const Person = () => {
  const { checkPermission, ToastComponent, hasPermission } = usePermissions();
  const { permissions } = usePermissionsContext();
  const [persons, setPersons] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [numberOfElements, setNumberOfElements] = useState(0);
  const [showCreatePersonModal, setShowCreatePersonModal] = useState(false);
  const [showEditPersonModal, setShowEditPersonModal] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const previousFilterParams = useRef({});
  const [filterParams, setFilterParams] = useState(() => mapPersonTypes());

  // Recalcula permissões sempre que o objeto permissions mudar
  const canView = useMemo(() => hasPermission('personView'), [hasPermission, permissions]);
  const canCreate = useMemo(() => hasPermission('personCreate'), [hasPermission, permissions]);
  const canUpdate = useMemo(() => hasPermission('personUpdate'), [hasPermission, permissions]);

  const fetchPersons = useCallback(async (filters = filterParams, currentPage = page, currentPageSize = pageSize) => {
    if (!canView) return;
    try {
      const data = await getAllPersons(filters, currentPage - 1, currentPageSize);
      setPersons(data.content);
      setTotalPages(data.totalPages);
      setTotalElements(data.totalElements);
      setNumberOfElements(data.numberOfElements);
    } catch (error) {
      console.error("Erro ao buscar pessoas:", error);
    }
  }, [filterParams, page, pageSize, canView]);

  useEffect(() => {
    if (canView) {
      fetchPersons();
    }
  }, [page, pageSize, fetchPersons, canView]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value, 10));
    setPage(1);
  };

  const handleOpenCreatePersonModal = () => {
    if (!checkPermission('personCreate')) {
      return;
    }
    setShowCreatePersonModal(true);
  };
  const handleCloseCreatePersonModal = () => setShowCreatePersonModal(false);

  const handleOpenEditPersonModal = (person) => {
    if (!checkPermission('personUpdate')) {
      return;
    }
    setSelectedPerson(person);
    setShowEditPersonModal(true);
  };
  
  const handleCloseEditPersonModal = () => {
    setSelectedPerson(null);
    setShowEditPersonModal(false);
  };
  
  const handleFilter = (filters) => {
    if (!canView) return;
    const filtersChanged = JSON.stringify(previousFilterParams.current) !== JSON.stringify(filters);

    if (filtersChanged) {
      setFilterParams(filters);
      setPage(1);
      fetchPersons(filters, 1, pageSize);
      previousFilterParams.current = filters;
    }
  };

  const handleClearFilter = () => {
    if (!canView) return;
    const defaultFilters = mapPersonTypes();
    setFilterParams(defaultFilters);
    setPage(1);
    fetchPersons(defaultFilters, 1, pageSize);
    previousFilterParams.current = defaultFilters;
  };

  if (!canView) {
    return (
      <>
        <ToastComponent />
        <Container>
          <h3 className="mb-4 mt-2 text-center text-md-start">Pessoas</h3>
        </Container>
      </>
    );
  }

  return (
    <Container>
      <ToastComponent />
      <h3 className="mb-4 mt-2 text-center text-md-start">Pessoas</h3>
      <Row className="justify-content-between align-items-center mb-3">
        <Col xs={12} md="auto" className="d-flex align-items-center justify-content-md-end justify-content-center">
          <Form.Label className="me-2 mb-0">Mostrar</Form.Label>
          <Form.Select
            value={pageSize}
            onChange={handlePageSizeChange}
            style={{ width: 'auto' }}
            className="me-2"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </Form.Select>
        </Col>
        <Col xs={12} md="auto" className="d-flex justify-content-md-end justify-content-center mt-2 mt-md-0">
          {canCreate && (
            <Button variant="primary" className="button-custom" onClick={handleOpenCreatePersonModal}>
              Cadastrar Pessoa
            </Button>
          )}
        </Col>
      </Row>
      {canView && <PersonFilter onFilter={handleFilter} onClear={handleClearFilter} />}
      <div style={{ overflowX: 'auto' }}>
        <Table striped bordered hover responsive="sm">
          <thead>
            <tr>
              <th>Código - Nome</th>
              <th>Documento</th>
              <th>Telefone</th>
              <th style={{textAlign: 'center', width: '50px' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {persons.map((person) => (
              <tr key={person.id}>
                <td>{person.oldId + ' - ' + person.name}</td>
                <td>{person.document}</td>
                <td>{person.cell_phone}</td>
                <td style={{textAlign: 'center'}}>
                  {canUpdate && (
                    <Button
                      variant="link"
                      onClick={() => handleOpenEditPersonModal(person)}
                      className="p-0"
                      style={{ fontSize: '1.2rem' }}
                    >
                      <FaEdit className='icon-custom'/>
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <PaginationComponent
        totalPages={totalPages}
        currentPage={page}
        onPageChange={handlePageChange}
        totalElements={totalElements}
        size={pageSize}
        pageNumber={page - 1}
        numberOfElements={numberOfElements}
      />

      <CreatePersonModal
        show={showCreatePersonModal}
        handleClose={handleCloseCreatePersonModal}
        refreshPersons={fetchPersons}
      />

      <EditPersonModal
        show={showEditPersonModal}
        handleClose={handleCloseEditPersonModal}
        person={selectedPerson}
        refreshPersons={fetchPersons}
      />
    </Container>
  );
};

export default Person;